import { useEffect, useState, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { http, inr, apiError, mediaUrl } from "@/lib/api";
import { LoginScreen, KPI, SlotGrid, Legend, StatusBadge, Modal, Logo, EmptyState, slotsSummary } from "@/components/shared";
import { Shell, PageHead, Card } from "@/components/Shell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem, SelectGroup, SelectLabel } from "@/components/ui/select";
import { toast } from "sonner";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell,
} from "recharts";
import {
  LayoutDashboard, Grid3x3, Building2, Users, Wallet,
  AlertTriangle, Inbox, Loader2, Plus, Car, PlayCircle,
  CheckCircle2, XCircle, Lightbulb, ArrowRight, RefreshCw, Shuffle, Monitor,
  Battery, Signal, Smartphone, MapPin, Download, ArrowUpRight, Info, Search, Eye,
} from "lucide-react";

const ACCENT = "#35A787";
const SCREENS = ["dashboard", "fleet", "advertisers", "drivers", "payouts", "incidents", "requests"];
const NAV = (pending) => [
  { key: "dashboard", label: "Dashboard", icon: LayoutDashboard },
  { key: "fleet", label: "Fleet & Slots", icon: Grid3x3 },
  { key: "advertisers", label: "Advertisers", icon: Building2 },
  { key: "drivers", label: "Drivers", icon: Users },
  { key: "payouts", label: "Payouts", icon: Wallet },
  { key: "incidents", label: "Incidents", icon: AlertTriangle },
  { key: "requests", label: "Requests", icon: Inbox, badge: pending > 0 ? pending : undefined },
];

// Online/liveness state for a cab screen, from its last check-in time. Lets
// ops see at a glance whether a client's ad change has actually reached the
// cab (green = reachable now), still pending (amber), or is offline (red).
function freshen(cab) {
  const last = cab.last_checkin ? new Date(cab.last_checkin).getTime() : null;
  const now = Date.now();
  const mins = last ? Math.round((now - last) / 60000) : null;
  if (mins == null)
    return { tint: "text-red-500", dot: "bg-red-500", label: "Never connected" };
  const hasContent = !!cab.content_fingerprint;
  if (mins <= 2)
    return { tint: "text-emerald-600", dot: "bg-emerald-500", label: hasContent ? "Online · synced" : "Online" };
  if (mins <= 10)
    return { tint: "text-amber-600", dot: "bg-amber-500", label: `Online · last seen ${mins}m ago` };
  return { tint: "text-red-500", dot: "bg-red-500", label: `Offline · last seen ${mins > 120 ? `${Math.round(mins / 60)}h` : `${mins}m`} ago` };
}

export default function OpsPortal() {
  const { user } = useAuth();
  const { screen } = useParams();
  const navigate = useNavigate();
  const [pending, setPending] = useState(0);
  const go = (s) => navigate(`/ops/${s}`);
  const active = SCREENS.includes(screen) ? screen : "dashboard";

  const refreshPending = useCallback(() => {
    http.get("/cab-requests?status=pending").then((r) => setPending(r.data.length)).catch(() => {});
  }, []);

  useEffect(() => {
    refreshPending();
    const t = setInterval(refreshPending, 30000);
    return () => clearInterval(t);
  }, [refreshPending]);

  if (user === null) return <FullLoader />;
  if (!user || user.portal !== "ops")
    return <LoginScreen portal="ops" demoCreds={[
      { id: "jayborana3@gmail.com", pw: "demo1234", label: "Owner" },
      { id: "admin@anand.in", pw: "demo1234", label: "Admin" },
      { id: "reviewer@anand.in", pw: "demo1234", label: "Reviewer" },
    ]} />;

  return (
    <Shell accent={ACCENT} portalName="Control Hub" nav={NAV(pending)} active={active} onNav={go}
      bell={{ count: pending, onClick: () => go("requests") }}>
      {active === "dashboard" && <Dashboard go={go} />}
      {active === "fleet" && <Fleet />}
      {active === "advertisers" && <Advertisers />}
      {active === "drivers" && <Drivers />}
      {active === "payouts" && <Payouts />}
      {active === "incidents" && <Incidents />}
      {active === "requests" && <Requests onDecided={refreshPending} />}
    </Shell>
  );
}

const FullLoader = () => <div className="flex min-h-screen items-center justify-center"><Loader2 className="animate-spin text-stone-400" /></div>;

function useFetch(url, dep = []) {
  const [data, setData] = useState(null);
  const reload = useCallback(() => { http.get(url).then((r) => setData(r.data)).catch(() => {}); }, [url]);
  useEffect(() => { reload(); // eslint-disable-next-line react-hooks/exhaustive-deps
  }, dep);
  return [data, reload, setData];
}

function RecommendationBanner({ rec, action }) {
  const tint = rec.feasible ? "#10B981" : "#F59E0B";
  return (
    <Card className="mb-6 flex flex-wrap items-start gap-4 p-4 sm:items-center">
      <span className="flex h-9 w-9 flex-none items-center justify-center rounded-md" style={{ background: tint + "14", color: tint }}>
        <Lightbulb size={16} />
      </span>
      <div className="min-w-0 flex-1">
        <div className="text-[11px] font-semibold uppercase tracking-wider" style={{ color: tint }}>Recommendation</div>
        <div className="mt-0.5 text-sm text-stone-600">{rec.note}</div>
      </div>
      {action}
    </Card>
  );
}

// ---------------- Dashboard ----------------
const YELLOW = "#FFC400";
const GREEN = "#35A787";
const GREEN_DEEP = "#2D8F74";
const RED = "#D64545";
const GRAY = "#767570";

function playsSeries(playsToday, booked) {
  const days = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
  const perCab = booked > 0 ? Math.round(playsToday / booked) : 0;
  const out = days.map((d, i) => {
    const shape = [0.86, 0.94, 1.03, 0.98, 1.12, 1.08, 0.79][i];
    return { day: d, plays: Math.round((booked || 1) * perCab * shape) };
  });
  return out;
}

function Dashboard({ go }) {
  const [kpi] = useFetch("/ops/dashboard");
  const [cabs] = useFetch("/cabs");
  const [avail] = useFetch("/slots/availability");
  if (!kpi || !cabs || !avail) return <FullLoader />;
  const slots = slotsSummary(cabs);
  const rec = avail.recommendation;

  const plays = playsSeries(kpi.plays_today, kpi.booked);

  const exportCSV = () => {
    const head = "Metric,Value\n" +
      `Total cabs,${kpi.total_cabs}\nBooked,${kpi.booked}\nAvailable,${kpi.available}\n` +
      `Plays today,${kpi.plays_today}\nPending reviews,${kpi.pending_reviews}\n` +
      `Pending requests,${kpi.pending_requests}\nOverdue photos,${kpi.overdue_photos}\n\nCab,Advertiser,Status\n` +
      cabs.map((c) => `${c.plate},${c.advertiser_name || "-"},${c.status}`).join("\n");
    const blob = new Blob([head], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = "anand-dashboard-report.csv"; a.click();
    URL.revokeObjectURL(url);
    toast.success("Report exported");
  };

  const alertCfg = kpi.overdue_photos > 0
    ? { tint: RED, icon: <AlertTriangle size={16} />, title: `${kpi.overdue_photos} compliance photo${kpi.overdue_photos > 1 ? "s" : ""} overdue`, sub: "Compliance photos are past due on live screens. Drivers may face a hold — review and follow up to keep inventory on the road." }
    : rec
    ? { tint: YELLOW, icon: <Lightbulb size={16} />, title: rec.feasible ? "Optimization opportunity" : "Cabs needed for demand", sub: rec.note, action: rec.feasible ? <Button size="sm" className="flex-none bg-[#35A787] text-white hover:bg-[#2D8F74]" onClick={async () => { await http.post("/cabs/assign-batch", { advertiser_id: rec.advertiser_id, count: rec.cabs_needed }); toast.success(`Assigned ${rec.cabs_needed} cabs to ${rec.advertiser_name}`); go("fleet"); }}>Assign now</Button> : undefined }
    : { tint: YELLOW, icon: <Info size={16} />, title: "All systems operational", sub: "Fleet is healthy and ad rotations are streaming. No actions required right now." };

  return (
    <>
      <PageHead title="Control Hub"
        sub="Founder dashboard — supply, demand & fleet health at a glance."
        action={<div className="flex items-center gap-2">
          <Button variant="outline" onClick={exportCSV} className="gap-2"><Download size={15} /> Export Report</Button>
        </div>} />

      <div className="mb-8 mt-2 h-px w-full bg-[#E5E7EB]" />

      {/* 3 KPI cards — one accent color per metric */}
      <div className="grid gap-8 sm:grid-cols-2 lg:grid-cols-3">
        <KPI label="Total cabs" value={kpi.total_cabs} sub={`${kpi.available} available · ${slots.used}/${slots.total} slots filled`} icon={Car} accent={GREEN_DEEP} delta={6} />
        <KPI label="On the road" value={kpi.booked} sub={`${slots.used}/${slots.total} slots filled`} icon={Car} accent={GREEN} delta={6} />
        <KPI label="Plays today" value={kpi.plays_today.toLocaleString("en-IN")} sub={kpi.plays_live ? "Live device data" : "Simulated baseline"} icon={PlayCircle} accent={YELLOW} delta={18} />
      </div>

      {/* Alert line */}
      <div className="mt-6 flex items-start gap-3 rounded-lg px-4 py-3" style={{ background: alertCfg.tint + "0d", borderLeft: `3px solid ${alertCfg.tint}` }}>
        <span className="mt-0.5 flex h-7 w-7 flex-none items-center justify-center rounded-md" style={{ background: alertCfg.tint + "1f", color: alertCfg.tint }}>{alertCfg.icon}</span>
        <div className="min-w-0 flex-1">
          <div className="text-sm font-semibold text-[#1F2937]">{alertCfg.title}</div>
          <div className="mt-0.5 text-xs text-[#6B6659]">{alertCfg.sub}</div>
        </div>
        {alertCfg.action}
      </div>

      {/* Plays chart */}
      <div className="mt-6">
        <div className="chart-panel">
          <div className="panel-head">
            <div>
              <h3 className="font-display text-[15px] font-semibold tracking-tight text-[#1F2937]">Plays by day</h3>
              <p className="mt-0.5 text-xs text-[#6B6659]">Ad plays per day across the fleet</p>
            </div>
            <div className="flex items-center gap-1.5 rounded-full bg-emerald-50 px-2.5 py-1 text-[11px] font-semibold text-[#0B6E4F]"><ArrowUpRight size={12} /> {kpi.plays_live ? "Live" : "Simulated"}</div>
          </div>
          <div className="panel-body h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={plays} margin={{ top: 6, right: 6, left: -8, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#EEE9DE" vertical={false} />
                <XAxis dataKey="day" tick={{ fontSize: 11, fill: GRAY }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 11, fill: GRAY }} axisLine={false} tickLine={false} tickFormatter={(v) => (v >= 1000 ? (v / 1000).toFixed(1) + "k" : v)} />
                <Tooltip formatter={(v) => [Number(v).toLocaleString("en-IN"), "Plays"]} cursor={{ fill: "rgba(255,196,0,0.08)" }} contentStyle={{ borderRadius: 10, border: "1px solid #E5E7EB", boxShadow: "0 8px 24px -8px rgba(16,24,40,0.2)", fontSize: 12 }} />
                <Bar dataKey="plays" radius={[6, 6, 0, 0]}>
                  {plays.map((p, i) => <Cell key={i} fill={i === plays.length - 1 ? YELLOW : GREEN} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* Data table: top cabs */}
      <div className="chart-panel mt-6">
        <div className="panel-head">
          <div>
            <h3 className="font-display text-[15px] font-semibold tracking-tight text-[#1F2937]">Fleet snapshot</h3>
            <p className="mt-0.5 text-xs text-[#6B6659]">{cabs.length} cabs · live inventory and slot status</p>
          </div>
          <Button variant="ghost" size="sm" onClick={() => go("fleet")}>View all <ArrowRight className="h-3.5 w-3.5" /></Button>
        </div>
        <div className="panel-body overflow-x-auto">
          <table className="ent-table">
            <thead>
              <tr><th>Cab</th><th>Advertiser</th><th>Status</th><th>Slots</th><th>App</th></tr>
            </thead>
            <tbody>
              {cabs.slice(0, 8).map((c) => {
                const used = c.slots_used ?? (c.status === "booked" && c.advertiser_id ? 1 : 0);
                const total = c.slots_total ?? 4;
                return (
                  <tr key={c.id}>
                    <td className="num font-medium text-[#1F2937]">{c.plate}</td>
                    <td>{c.advertiser_name || <span className="text-[#9CA3AF]">—</span>}</td>
                    <td><StatusBadge status={c.status} /></td>
                    <td className="num">{used}/{total} <span className="text-[#9CA3AF]">used</span></td>
                    <td className="num text-[#6B6659]">{c.app_version ? `v${c.app_version}` : "—"}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
}

// ---------------- Fleet & slot management ----------------
function Fleet() {
  const [cabs, reload] = useFetch("/cabs");
  const [advs] = useFetch("/advertisers");
  const [avail] = useFetch("/slots/availability");
  const [ads] = useFetch("/creatives/on-air");
  const [drivers] = useFetch("/drivers");
  const { config, setConfig } = useAuth();
  const [selected, setSelected] = useState(null);
  const [addOpen, setAddOpen] = useState(false);
  const [plate, setPlate] = useState("");
  const [maxCabs, setMaxCabs] = useState("");
  const [shuffling, setShuffling] = useState(false);
  const [search, setSearch] = useState("");

  if (!cabs || !advs || !avail || !ads || !drivers) return <FullLoader />;
  const driverByCab = {};
  (drivers || []).forEach((d) => { if (d.cab_id) driverByCab[d.cab_id] = d; });
  const availCabs = cabs.filter((c) => c.status === "available").length;
  const booked = cabs.filter((c) => c.status === "booked").length;
  const slots = slotsSummary(cabs);
  const rec = avail.recommendation;
  const q = search.trim().replace(/[\s-]/g, "").toLowerCase();
  const results = q ? cabs.filter((c) => c.plate.replace(/[\s-]/g, "").toLowerCase().includes(q)) : [];

  const addCab = async () => {
    if (!plate.trim()) return;
    await http.post("/cabs", { plate });
    setPlate(""); setAddOpen(false); reload(); toast.success("Cab added");
  };
  const saveMax = async () => {
    const r = await http.put("/config/fleet", { max_cabs: Number(maxCabs) });
    setConfig({ ...config, fleet: r.data }); toast.success("Max cabs updated");
  };
  const runRec = async () => {
    await http.post("/cabs/assign-batch", { advertiser_id: rec.advertiser_id, count: Math.min(rec.cabs_needed, availCabs) });
    toast.success(`Assigned ${rec.cabs_needed} cabs to ${rec.advertiser_name}`); setSelected(null); reload();
  };
  const shuffleAll = async () => {
    setShuffling(true);
    try { const r = await http.post("/cabs/shuffle-all"); toast.success(`Shuffled slots on ${r.data.cabs} booked cabs`); reload(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setShuffling(false); }
  };

  return (
    <>
      <PageHead title="Fleet & Slots" sub={`${cabs.length} cabs · ${availCabs} free · ${booked} booked · ${slots.free} open slots`}
        action={<div className="flex gap-2">
          <Button variant="outline" onClick={shuffleAll} disabled={shuffling}>
            {shuffling ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
            {shuffling ? "Shuffling…" : "Shuffle all slots"}
          </Button>
          <Button data-testid="admin-fleet-add-cab-btn" onClick={() => setAddOpen(true)}><Plus className="h-4 w-4" /> Add cab</Button>
        </div>} />

      {cabs.some((c) => c.app_version) && (() => {
        const byV = {};
        cabs.forEach((c) => { if (c.app_version) byV[c.app_version] = (byV[c.app_version] || 0) + 1; });
        const known = Object.values(byV).reduce((a, b) => a + b, 0);
        return (
          <div className="mb-3 flex flex-wrap items-center gap-2 text-xs text-stone-500">
            <span className="font-semibold text-stone-600">App version</span>
            {Object.entries(byV).sort().map(([v, n]) => (
              <span key={v} className="rounded-full border border-stone-200 bg-stone-50 px-2.5 py-1 font-medium text-stone-700">v{v} · {n} cab{n > 1 ? "s" : ""}</span>
            ))}
            {known < cabs.length && <span className="text-stone-400">{cabs.length - known} no version yet</span>}
          </div>
        );
      })()}

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <KPI label="Cabs available" value={availCabs} sub={`${slots.free} open slots`} icon={Car} accent={ACCENT} />
        <KPI label="Cabs booked" value={booked} sub={`${slots.used} of ${slots.total} slots used`} accent={ACCENT} />
        <KPI label="Revenue potential" value={inr(availCabs * (config?.rules?.ADS_PER_MINUTE || 4) * (config?.rules?.PRICE_PER_SLOT || 4300))} sub="if every free slot is sold" accent={ACCENT} />
        <Card>
          <div className="text-[11px] font-medium uppercase tracking-wider text-stone-500">Max cabs (cap)</div>
          <div className="mt-3 flex gap-2">
            <Input value={maxCabs} onChange={(e) => setMaxCabs(e.target.value)} placeholder={String(config?.fleet?.max_cabs || 200)} className="font-mono" />
            <Button variant="secondary" onClick={saveMax}>Set</Button>
          </div>
        </Card>
      </div>

      {rec && <RecommendationBanner rec={rec} action={rec.feasible && (
        <Button size="sm" className="flex-none" onClick={runRec}>Assign {rec.cabs_needed} now</Button>
      )} />}

      <Card className="mt-6">
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
          <div className="flex flex-wrap items-center gap-3">
            <h3 className="font-display text-[15px] font-semibold tracking-tight text-stone-900">Slot grid — click a cell to manage</h3>
            <div className="relative">
              <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-stone-400" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search plate… e.g. RL-3240"
                data-testid="fleet-search"
                className="h-8 w-52 rounded-md border border-stone-200 bg-stone-50 pl-8 text-sm focus:border-[#35A787] focus:bg-white focus:outline-none focus:ring-2 focus:ring-[#35A787]/20"
              />
            </div>
          </div>
          <Legend />
        </div>

        {search.trim() && (
          <div className="mb-4 space-y-2">
            <div className="text-xs font-medium text-stone-500">
              {results.length === 0 ? "No cab found for that plate" : `${results.length} cab${results.length > 1 ? "s" : ""} found — click to manage`}
            </div>
            {results.map((c) => (
              <button
                key={c.id}
                onClick={() => setSelected(c)}
                className="w-full rounded-lg border border-stone-200 bg-white p-3.5 text-left transition hover:border-[#35A787]/50 hover:bg-[#35A787]/5"
              >
                <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                  <span className="font-display text-lg font-bold tracking-wide text-stone-900">{c.plate}</span>
                  <StatusBadge status={c.status} />
                  {c.advertiser_name && <span className="text-sm font-medium text-stone-600">{c.advertiser_name}</span>}
                  {driverByCab[c.id] && (
                    <span className="flex items-center gap-1.5 text-xs text-stone-500">
                      <Users className="h-3.5 w-3.5 text-stone-400" />
                      {driverByCab[c.id].name}
                    </span>
                  )}
                  <span className="ml-auto flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-stone-500">
                    {c.zone && <span className="flex items-center gap-1"><MapPin className="h-3.5 w-3.5" /> {c.zone}</span>}
                    <span className="flex items-center gap-1"><Monitor className="h-3.5 w-3.5" /> {c.app_version ? `v${c.app_version}` : "no version yet"}</span>
                  </span>
                </div>
                <div className="mt-2 flex flex-wrap items-center gap-1.5">
                  {(c.slots?.length ? c.slots : Array.from({ length: c.slots_total ?? 4 }, (_, i) => ({ slot: i + 1 }))).map((s) => (
                    <span key={s.slot} className="flex items-center gap-1.5 rounded-full border border-stone-200 bg-stone-50 px-2.5 py-1 text-xs">
                      <span className="font-bold text-stone-400">S{s.slot}</span>
                      <PlayCircle className={`h-3.5 w-3.5 ${s.advertiser_name ? "text-[#35A787]" : "text-stone-300"}`} />
                      {s.advertiser_name ? (
                        <span className="font-medium text-stone-700">{s.advertiser_name}{s.title ? ` · ${s.title}` : ""}</span>
                      ) : (
                        <span className="text-stone-400">empty slot</span>
                      )}
                    </span>
                  ))}
                </div>
              </button>
            ))}
          </div>
        )}

        <SlotGrid cabs={search.trim() && q ? results : cabs} onCellClick={setSelected} />
      </Card>

      <Modal open={!!selected} onClose={() => setSelected(null)} wide title={selected ? `Cab ${selected.plate}` : ""}>
        {selected && <CabManager cab={selected} advs={advs} ads={ads} rec={rec} driver={driverByCab[selected.id]} onDone={() => { setSelected(null); reload(); }} />}
      </Modal>

      <Modal open={addOpen} onClose={() => setAddOpen(false)} title="Add a cab">
        <label className="mb-1.5 block text-xs font-medium text-stone-500">Plate number</label>
        <Input value={plate} onChange={(e) => setPlate(e.target.value)} placeholder="MH-12-AB-1234" className="mb-4" />
        <Button onClick={addCab} className="w-full">Add cab</Button>
      </Modal>
    </>
  );
}

function CabManager({ cab, advs, ads, rec, driver, onDone }) {
  const [advId, setAdvId] = useState(rec?.advertiser_id || advs[0]?.id || "");
  const [slotBusy, setSlotBusy] = useState(0);
  const [preview, setPreview] = useState(null); // { creative, slot }
  const setStatus = async (status) => { await http.patch(`/cabs/${cab.id}`, { status }); toast.success(`Marked ${status}`); onDone(); };
  const assign = async () => { try { await http.post(`/cabs/${cab.id}/assign`, { advertiser_id: advId }); toast.success("Assigned"); onDone(); } catch (e) { toast.error(apiError(e.response?.data?.detail)); } };
  const unassign = async () => { await http.post(`/cabs/${cab.id}/unassign`); toast.success("Unassigned"); onDone(); };
  const changeSlot = async (slot, creativeId) => {
    setSlotBusy(slot);
    try { await http.put(`/cabs/${cab.id}/slots/${slot}`, { creative_id: creativeId }); toast.success(`Slot ${slot} updated`); onDone(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setSlotBusy(0); }
  };
  // Selecting an ad opens the simulator first; nothing goes live until "Put in slot".
  const pickSlot = (slot, creativeId) => {
    if (!creativeId) return changeSlot(slot, "");
    const cr = (ads || []).find((a) => a.id === creativeId);
    if (cr) setPreview({ creative: cr, slot });
    else changeSlot(slot, creativeId);
  };
  const confirmSlot = async () => {
    const { slot, creative } = preview;
    setPreview(null);
    await changeSlot(slot, creative.id);
  };
  const previewSlot = (slot) => {
    const cr = (ads || []).find((a) => a.id === slot.creative_id);
    if (cr) setPreview({ creative: cr, slot: slot.slot, viewing: true });
  };
  const shuffle = async () => {
    await http.post(`/cabs/${cab.id}/shuffle`); toast.success("Slots reshuffled"); onDone();
  };
  const total = cab.slots_total || 4;
  // Group the on-air ads by advertiser so ops can see each brand's ads at a glance.
  const adsByAdv = (ads || []).reduce((m, a) => {
    const k = a.advertiser_name || "—";
    (m[k] = m[k] || []).push(a);
    return m;
  }, {});
  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2"><StatusBadge status={cab.status} />{cab.advertiser_name && <span className="text-sm text-stone-500">· {cab.advertiser_name}</span>}
        {cab.app_version && <span className="rounded-full bg-stone-100 px-2 py-0.5 text-[11px] font-semibold tabular-nums text-stone-600">v{cab.app_version}</span>}
        <span className="ml-auto">
          <Button variant="outline" size="sm" onClick={() => window.open(`/cab/${cab.plate}`, "_blank")}><Monitor className="h-3.5 w-3.5" /> Open tablet view</Button>
        </span>
      </div>
      <div className="flex flex-wrap items-center gap-x-5 gap-y-1.5 rounded-lg border border-stone-200 bg-stone-50 px-4 py-3 text-xs text-stone-600">
        <span className="flex items-center gap-1.5"><MapPin className="h-3.5 w-3.5 text-stone-400" /> {cab.zone || "—"}</span>
        <span className="flex items-center gap-1.5"><Battery className="h-3.5 w-3.5 text-stone-400" /> {cab.battery != null ? `${cab.battery}%` : "—"}</span>
        <span className="flex items-center gap-1.5"><Signal className="h-3.5 w-3.5 text-stone-400" /> {cab.signal ? "▮".repeat(cab.signal) + "▯".repeat(4 - cab.signal) : "—"}</span>
        <span className="flex items-center gap-1.5"><Smartphone className="h-3.5 w-3.5 text-stone-400" /> {cab.app_version ? `v${cab.app_version}` : "no version yet"}</span>
        <span className={`ml-auto flex items-center gap-1.5 font-medium ${freshen(cab).tint}`}>
          <span className={`inline-block h-2 w-2 rounded-full ${freshen(cab).dot}`} />
          {freshen(cab).label}
        </span>
        <span className="flex items-center gap-1.5"><RefreshCw className="h-3.5 w-3.5 text-stone-400" /> {cab.last_checkin ? new Date(cab.last_checkin).toLocaleString("en-IN") : "no check-in yet"}</span>
      </div>
      <div className="rounded-lg border border-stone-200 p-4">
        <div className="mb-3 flex items-center justify-between">
          <label className="flex items-center gap-2 text-xs font-semibold text-stone-600"><Users className="h-3.5 w-3.5 text-stone-400" /> Cab driver</label>
        </div>
        {driver ? (
          <div className="grid grid-cols-2 gap-x-5 gap-y-2.5 text-sm sm:grid-cols-3">
            <div className="col-span-2 sm:col-span-3">
              <div className="font-semibold text-stone-900">{driver.name}</div>
              <a href={`tel:${driver.phone}`} className="mt-0.5 inline-flex items-center gap-1.5 text-xs font-medium text-[#35A787] hover:underline">{driver.phone}</a>
            </div>
            <div><div className="text-[11px] uppercase tracking-wider text-stone-400">KYC</div><div className="mt-1"><StatusBadge status={driver.kyc_status === "verified" ? "verified" : "pending"} /></div></div>
            <div><div className="text-[11px] uppercase tracking-wider text-stone-400">Compliance photo</div><div className="mt-1"><StatusBadge status={driver.compliance} /></div></div>
            <div><div className="text-[11px] uppercase tracking-wider text-stone-400">Payout UPI</div><div className="mt-1 font-mono text-xs text-stone-700">{driver.payout_upi || "—"}</div></div>
            <div><div className="text-[11px] uppercase tracking-wider text-stone-400">Last photo</div><div className="mt-1 font-medium text-stone-800">{driver.last_photo_date ? new Date(driver.last_photo_date).toLocaleDateString("en-IN") : "—"}</div></div>
          </div>
        ) : (
          <div className="text-sm text-stone-400">No driver assigned to this cab yet.</div>
        )}
      </div>
      <div className="flex flex-wrap items-center gap-3 rounded-lg border border-stone-200 bg-stone-50 px-4 py-3">
        <span className="text-xs font-medium text-stone-500">This cab screen has {total} slots</span>
        <span className="flex gap-1.5">
          {Array.from({ length: total }).map((_, i) => (
            <span key={i} className={i < (cab.slots_used || 0)
              ? "flex h-6 w-6 items-center justify-center rounded-md bg-amber-100 text-[10px] font-bold tabular-nums text-amber-700"
              : "flex h-6 w-6 items-center justify-center rounded-md bg-stone-100 text-[10px] font-bold tabular-nums text-stone-500"}>{i + 1}</span>
          ))}
        </span>
        <span className="ml-auto text-xs font-medium tabular-nums text-stone-500">{cab.slots_used || 0} used · {cab.slots_free ?? total} free</span>
      </div>
      <div className="rounded-lg border border-stone-200 p-4">
        <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
          <label className="text-xs font-semibold text-stone-600">Ads in each slot — pick a brand's ad, preview it, then put it live</label>
          <Button variant="outline" size="sm" onClick={shuffle}><Shuffle className="h-3.5 w-3.5" /> Shuffle this cab</Button>
        </div>
        <div className="space-y-2">
          {(cab.slots || []).map((s) => (
            <div key={s.slot} className="flex items-center gap-2">
              <span className="flex h-6 w-6 flex-none items-center justify-center rounded-md bg-stone-100 text-[10px] font-bold text-stone-500">S{s.slot}</span>
              <span className="min-w-0 flex-1 truncate text-sm text-stone-700">{s.advertiser_name || <span className="text-stone-400">Empty slot</span>}</span>
              {s.creative_id && (
                <Button variant="outline" size="sm" className="h-8 flex-none px-2" disabled={slotBusy === s.slot} onClick={() => previewSlot(s)}>
                  <Eye className="h-3.5 w-3.5" /> Preview
                </Button>
              )}
              <Select value={s.creative_id || "__none__"} disabled={slotBusy === s.slot} onValueChange={(v) => pickSlot(s.slot, v === "__none__" ? "" : v)}>
                <SelectTrigger className="h-8 w-full max-w-[45%] px-2 text-xs"><SelectValue placeholder="— empty —" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">empty slot</SelectItem>
                  {Object.entries(adsByAdv).map(([advName, list]) => (
                    <SelectGroup key={advName}>
                      <SelectLabel className="px-2 py-1.5 text-[11px] font-semibold uppercase tracking-wider text-stone-400">{advName}</SelectLabel>
                      {list.map((a) => <SelectItem key={a.id} value={a.id}>{a.title} · {a.duration}s</SelectItem>)}
                    </SelectGroup>
                  ))}
                </SelectContent>
              </Select>
            </div>
          ))}
        </div>
        <p className="mt-3 text-[11px] text-stone-400">Pick an ad to see the cab-screen preview first — it only goes live when you tap “Put in slot”.</p>
      </div>
      {cab.status === "available" && (
        <div className="rounded-lg border border-stone-200 p-4">
          <label className="mb-2 block text-xs font-semibold text-stone-600">Assign to advertiser</label>
          <Select value={advId} onValueChange={(v) => setAdvId(v === "__none__" ? "" : v)}>
            <SelectTrigger className="mb-3"><SelectValue placeholder="— pick a brand —" /></SelectTrigger>
            <SelectContent>
              {!rec?.advertiser_id && <SelectItem value="__none__">— pick a brand —</SelectItem>}
              {advs.map((a) => <SelectItem key={a.id} value={a.id}>{a.name}{rec?.advertiser_id === a.id ? " (recommended)" : ""}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button onClick={assign} className="w-full">Assign slot</Button>
        </div>
      )}
      {cab.status === "booked" && <Button variant="destructive" className="w-full" onClick={unassign}>Unassign / free this slot</Button>}
      <div className="grid grid-cols-3 gap-2">
        {["available", "offline", "faulty"].map((s) => (
          <Button key={s} variant="outline" onClick={() => setStatus(s)} className="capitalize">{s}</Button>
        ))}
      </div>
      <CreativePreview
        creative={preview?.creative}
        onClose={() => setPreview(null)}
        subtitle={preview ? `${preview.creative.advertiser_name || "Ad"} · ${preview.creative.duration}s${preview.viewing ? ` · currently in slot ${preview.slot}` : ` · will play in slot ${preview.slot}`}` : ""}
        action={preview && !preview.viewing ? (
          <>
            <Button variant="outline" className="flex-1" onClick={() => setPreview(null)}>Cancel</Button>
            <Button className="flex-1" onClick={confirmSlot}>Put in slot {preview.slot}</Button>
          </>
        ) : null}
      />
    </div>
  );
}

// ---------------- Ad review queue ----------------
export function CreativePreview({ creative, onClose, action, subtitle }) {
  if (!creative) return null;
  return (
    <Modal open={!!creative} onClose={onClose} wide title="Cab-screen simulator (16:9)" testid="creative-preview-modal">
      <div className="relative overflow-hidden rounded-lg border border-stone-200">
        <div className="aspect-video">
          {creative.video ? (
            <video src={mediaUrl(creative.video)} controls autoPlay className="h-full w-full bg-slate-950 object-contain" />
          ) : (
            <div className="flex h-full w-full flex-col items-center justify-center bg-stone-50 text-center">
              <Logo size={44} accent={ACCENT} />
              <div className="mt-3 font-display text-lg font-bold text-stone-900">{creative.title}</div>
              <div className="text-xs text-stone-500">Headrest ad · {creative.duration}s</div>
            </div>
          )}
        </div>
        <div className="absolute bottom-0 left-0 h-0.5" style={{ background: ACCENT, animation: `progressBar ${creative.duration || 15}s linear infinite` }} />
      </div>
      <div className="mt-3 text-center text-xs text-stone-500">{subtitle || `Passenger view — ${creative.advertiser_name || "your ad"}`}</div>
      {action && <div className="mt-4 flex gap-2">{action}</div>}
    </Modal>
  );
}

// ---------------- Advertisers ----------------
function Advertisers() {
  const [advs, reload] = useFetch("/advertisers");
  const [cabs] = useFetch("/cabs");
  const [open, setOpen] = useState(false);
  const [detail, setDetail] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", gstin: "", category: "Retail" });
  if (!advs || !cabs) return <FullLoader />;
  const add = async () => {
    try { await http.post("/advertisers", form); toast.success("Advertiser added (login: demo1234)"); setOpen(false); setForm({ name: "", email: "", gstin: "", category: "Retail" }); reload(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const detailCabs = detail ? cabs.filter((c) => c.advertiser_id === detail.id) : [];
  return (
    <>
      <PageHead title="Advertisers" sub={`${advs.length} accounts`}
        action={<Button onClick={() => setOpen(true)}><Plus className="h-4 w-4" /> Add advertiser</Button>} />
      <div className="chart-panel mt-6">
        <div className="panel-head">
          <div>
            <h3 className="font-display text-[15px] font-semibold tracking-tight text-[#1F2937]">Advertiser accounts</h3>
            <p className="mt-0.5 text-xs text-[#6B6659]">Managed clients on the network · click a row for details & assigned cabs</p>
          </div>
        </div>
        <div className="panel-body overflow-x-auto">
          <table className="ent-table">
            <thead>
              <tr>{["Business", "Category", "Cabs", "Spend/mo", "GSTIN", "Renewal"].map((h) => <th key={h}>{h}</th>)}</tr>
            </thead>
            <tbody>
              {advs.map((a) => (
                <tr key={a.id} className="cursor-pointer" onClick={() => setDetail(a)}>
                  <td><div className="font-medium text-[#1F2937]">{a.name}</div><div className="text-xs text-[#6B6659]">{a.email}</div></td>
                  <td className="text-[#6B6659]">{a.category}</td>
                  <td className="num">{a.cabs_booked}</td>
                  <td className="num">{inr(a.spend)}</td>
                  <td className="num text-xs text-[#6B6659]">{a.gstin || "—"}</td>
                  <td className="text-[#6B6659]">{a.renewal_date}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      <Modal open={!!detail} onClose={() => setDetail(null)} title={detail ? `Advertiser · ${detail.name}` : ""}>
        {detail && (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3 rounded-lg border border-stone-200 bg-stone-50 p-3 text-sm">
              <div><div className="text-[11px] uppercase tracking-wider text-stone-400">Email</div><div className="font-medium text-stone-800">{detail.email}</div></div>
              <div><div className="text-[11px] uppercase tracking-wider text-stone-400">Category</div><div className="font-medium text-stone-800">{detail.category}</div></div>
              <div><div className="text-[11px] uppercase tracking-wider text-stone-400">GSTIN</div><div className="font-medium text-stone-800">{detail.gstin || "—"}</div></div>
              <div><div className="text-[11px] uppercase tracking-wider text-stone-400">Renewal</div><div className="font-medium text-stone-800">{detail.renewal_date}</div></div>
              <div><div className="text-[11px] uppercase tracking-wider text-stone-400">Cabs booked</div><div className="font-medium text-stone-800">{detail.cabs_booked}</div></div>
              <div><div className="text-[11px] uppercase tracking-wider text-stone-400">Spend / month</div><div className="font-medium text-stone-800">{inr(detail.spend)}</div></div>
            </div>
            <div>
              <h4 className="mb-2 text-xs font-semibold uppercase tracking-wider text-stone-500">Assigned cabs ({detailCabs.length})</h4>
              {detailCabs.length === 0 ? (
                <div className="rounded-lg border border-dashed border-stone-200 p-4 text-center text-sm text-stone-400">No cabs assigned yet</div>
              ) : (
                <div className="flex flex-wrap gap-2">
                  {detailCabs.map((c) => (
                    <span key={c.id} className="inline-flex items-center gap-1.5 rounded-full border border-stone-200 bg-stone-50 px-3 py-1.5 text-xs font-medium text-stone-700">
                      <Car size={12} className="text-stone-400" /> {c.plate} <StatusBadge status={c.status} />
                    </span>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </Modal>
      <Modal open={open} onClose={() => setOpen(false)} title="Add advertiser">
        <div className="space-y-3">
          {[["Business name", "name"], ["Email", "email"], ["GSTIN", "gstin"], ["Category", "category"]].map(([l, k]) => (
            <div key={k}><label className="mb-1 block text-xs font-medium text-stone-500">{l}</label>
              <Input value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })} /></div>
          ))}
          <Button onClick={add} className="w-full">Create account</Button>
        </div>
      </Modal>
    </>
  );
}

// ---------------- Drivers ----------------
function Drivers() {
  const [drivers, dReload] = useFetch("/drivers");
  const [photos, pReload] = useFetch("/compliance-photos");
  const [detail, setDetail] = useState(null);
  if (!drivers) return <FullLoader />;
  const overdue = drivers.filter((d) => d.compliance === "overdue");
  const pendingPhotos = (photos || []).filter((p) => p.status === "pending");
  const act = async (fn, ok) => { try { await fn(); toast.success(ok); dReload(); pReload(); } catch (e) { toast.error(apiError(e.response?.data?.detail)); } };
  const verifyKyc = (dd) => act(() => http.patch(`/drivers/${dd.id}/kyc/verify`), `${dd.name || "Driver"}'s KYC verified`);
  const okPhoto = (p) => act(() => http.patch(`/compliance-photos/${p.id}/verify`), "Photo verified");
  const nopePhoto = (p) => act(() => http.patch(`/compliance-photos/${p.id}/reject`), "Photo rejected");
  return (
    <>
      <PageHead title="Drivers" sub={`${drivers.length} drivers · ${overdue.length} compliance overdue · click a row for details`} />
      {overdue.length > 0 && (
        <div className="mb-4 flex items-center gap-2 rounded-lg border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
          <AlertTriangle size={16} /> {overdue.length} driver(s) overdue on compliance photo — flagged.
        </div>
      )}
      {pendingPhotos.length > 0 && (
        <div className="chart-panel">
          <div className="panel-head">
            <div>
              <h3 className="font-display text-[15px] font-semibold tracking-tight text-[#1F2937]">Compliance photo review</h3>
              <p className="mt-0.5 text-xs text-[#6B6659]">{pendingPhotos.length} photo(s) waiting for approval</p>
            </div>
          </div>
          <div className="panel-body grid grid-cols-2 gap-4 md:grid-cols-3">
            {pendingPhotos.map((p) => (
              <div key={p.id} className="overflow-hidden rounded-lg border border-stone-200">
                <img src={p.image} alt="compliance" className="aspect-[3/4] w-full object-cover" />
                <div className="p-3">
                  <div className="font-medium text-[#1F2937]">{p.driver_name}</div>
                  <div className="mt-0.5 text-xs text-[#6B6659]">{new Date(p.submitted_at).toLocaleString("en-IN")}</div>
                  <div className="mt-2.5 flex gap-2">
                    <Button size="sm" className="flex-1 bg-[#35A787] text-white hover:bg-[#2D8F74]" onClick={() => okPhoto(p)}>Verify</Button>
                    <Button size="sm" variant="outline" className="flex-1" onClick={() => nopePhoto(p)}>Reject</Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
      <div className="chart-panel mt-6">
        <div className="panel-head">
          <div>
            <h3 className="font-display text-[15px] font-semibold tracking-tight text-[#1F2937]">Driver directory</h3>
            <p className="mt-0.5 text-xs text-[#6B6659]">KYC and compliance status</p>
          </div>
        </div>
        <div className="panel-body overflow-x-auto">
          <table className="ent-table">
            <thead>
              <tr>{["Driver", "Cab", "KYC", "Payout UPI", "Compliance photo"].map((h) => <th key={h}>{h}</th>)}</tr>
            </thead>
            <tbody>
              {drivers.map((d) => (
                <tr key={d.id} className="cursor-pointer" onClick={() => setDetail(d)}>
                  <td><div className="font-medium text-[#1F2937]">{d.name}</div><div className="text-xs text-[#6B6659]">{d.phone}</div></td>
                  <td className="num text-[#374151]">{d.plate}</td>
                  <td><StatusBadge status={d.kyc_status === "verified" ? "verified" : "pending"} /></td>
                  <td className="num text-xs text-[#6B6659]">{d.payout_upi}</td>
                  <td><StatusBadge status={d.compliance} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      <Modal open={!!detail} onClose={() => setDetail(null)} title={detail ? `Driver · ${detail.name}` : ""}>
        {detail && (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3 rounded-lg border border-stone-200 bg-stone-50 p-3 text-sm">
              <div><div className="text-[11px] uppercase tracking-wider text-stone-400">Phone</div><div className="font-medium text-stone-800">{detail.phone}</div></div>
              <div><div className="text-[11px] uppercase tracking-wider text-stone-400">Cab</div><div className="font-medium text-stone-800">{detail.plate}</div></div>
              <div><div className="text-[11px] uppercase tracking-wider text-stone-400">KYC</div><div><StatusBadge status={detail.kyc_status === "verified" ? "verified" : "pending"} /></div></div>
              <div><div className="text-[11px] uppercase tracking-wider text-stone-400">Compliance photo</div><div><StatusBadge status={detail.compliance} /></div></div>
              <div><div className="text-[11px] uppercase tracking-wider text-stone-400">Payout UPI</div><div className="font-medium text-stone-800">{detail.payout_upi || "—"}</div></div>
              <div><div className="text-[11px] uppercase tracking-wider text-stone-400">Last photo</div><div className="font-medium text-stone-800">{detail.last_photo_date ? new Date(detail.last_photo_date).toLocaleDateString("en-IN") : "—"}</div></div>
            </div>
            {detail.kyc_status !== "verified" && (
              <button onClick={() => verifyKyc(detail)}
                className="flex w-full items-center justify-center gap-2 rounded-lg bg-[#35A787] py-2.5 text-sm font-semibold text-white transition-colors hover:bg-[#2D8F74]">
                <CheckCircle2 size={16} /> Mark KYC verified
              </button>
            )}
          </div>
        )}
      </Modal>
    </>
  );
}

// ---------------- Payouts ----------------
function Payouts() {
  const [data, reload] = useFetch("/payouts");
  const { config } = useAuth();
  if (!data) return <FullLoader />;
  const pay = async (o) => {
    try { await http.post("/payouts/run", { driver_id: o.driver_id, amount: o.amount }); toast.success(`Paid ${inr(o.amount)} to ${o.name}`); reload(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  return (
    <>
      <PageHead title="Payouts" sub={`Period ${data.period} · runs on 1st & 16th · ₹${config?.rules?.DRIVER_PAY_PER_CAB_MONTH ?? 300} per active cab`} />
      <div className="chart-panel mt-6">
        <div className="panel-head">
          <div>
            <h3 className="font-display text-[15px] font-semibold tracking-tight text-[#1F2937]">Payouts due</h3>
            <p className="mt-0.5 text-xs text-[#6B6659]">Cycle payout for drivers with an active cab</p>
          </div>
        </div>
        <div className="panel-body overflow-x-auto">
          <table className="ent-table">
            <thead>
              <tr>{["Driver", "KYC", "Owed", "UPI", "Action"].map((h) => <th key={h}>{h}</th>)}</tr>
            </thead>
            <tbody>
              {data.owed.map((o) => (
                <tr key={o.driver_id}>
                  <td className="font-medium text-[#1F2937]">{o.name}</td>
                  <td><StatusBadge status={o.kyc === "verified" ? "verified" : "pending"} /></td>
                  <td className="num">{inr(o.amount)}</td>
                  <td className="num text-xs text-[#6B6659]">{o.upi}</td>
                  <td>
                    {o.paid ? <span className="num text-xs font-medium text-emerald-600">{o.utr}</span> :
                      o.amount === 0 ? <span className="text-xs text-[#9CA3AF]">No active cab</span> :
                        <Button size="sm" onClick={() => pay(o)} disabled={o.kyc !== "verified"}>Run payout</Button>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      <h3 className="mt-8 font-display text-[15px] font-semibold tracking-tight text-[#1F2937]">Payout history</h3>
      <div className="chart-panel mt-4">
        <div className="panel-body overflow-x-auto">
          <table className="ent-table">
            <thead>
              <tr>{["Driver", "Amount", "UTR", "Period", "Date"].map((h) => <th key={h}>{h}</th>)}</tr>
            </thead>
            <tbody>
              {data.history.map((p) => (
                <tr key={p.id}>
                  <td className="text-[#1F2937]">{p.driver_name}</td>
                  <td className="num">{inr(p.amount)}</td>
                  <td className="num text-xs text-[#6B6659]">{p.utr}</td>
                  <td className="text-[#6B6659]">{p.period}</td>
                  <td className="text-[#6B6659]">{new Date(p.date).toLocaleDateString("en-IN")}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
}

// ---------------- Incidents ----------------
function Incidents() {
  const [items, reload] = useFetch("/incidents");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ title: "", severity: "medium", assignee: "Field Ops" });
  if (!items) return <FullLoader />;
  const add = async () => { if (!form.title.trim()) return; await http.post("/incidents", form); toast.success("Incident opened"); setOpen(false); setForm({ title: "", severity: "medium", assignee: "Field Ops" }); reload(); };
  const close = async (i) => { await http.patch(`/incidents/${i.id}/close`); toast.success("Closed"); reload(); };
  return (
    <>
      <PageHead title="Incidents" sub="Playbook: open → assign → resolve → close"
        action={<Button onClick={() => setOpen(true)}><Plus className="h-4 w-4" /> New incident</Button>} />
      <div className="space-y-3">
        {items.map((i) => (
          <Card key={i.id} className="flex items-center justify-between gap-3 p-4">
            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-2"><span className="font-semibold text-stone-900">{i.title}</span><StatusBadge status={i.status} /></div>
              <div className="mt-1 text-xs text-stone-500 capitalize">severity {i.severity} · {i.assignee}</div>
            </div>
            {i.status === "open" && <Button variant="outline" size="sm" className="flex-none" onClick={() => close(i)}>Close</Button>}
          </Card>
        ))}
      </div>
      <Modal open={open} onClose={() => setOpen(false)} title="Open incident">
        <div className="space-y-3">
          <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="What happened?" />
          <Select value={form.severity} onValueChange={(v) => setForm({ ...form, severity: v })}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              {["low", "medium", "high"].map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
            </SelectContent>
          </Select>
          <Input value={form.assignee} onChange={(e) => setForm({ ...form, assignee: e.target.value })} placeholder="Assign to" />
          <Button onClick={add} className="w-full">Open incident</Button>
        </div>
      </Modal>
    </>
  );
}

// ---------------- Cab requests (client wants more cabs) ----------------
const CAB_REJECT_REASONS = ["Fleet at capacity", "Below minimum criteria", "Customer not reachable", "Onboarding incomplete", "Other"];
function Requests({ onDecided }) {
  const [all, reload] = useFetch("/cab-requests");
  const [rejectFor, setRejectFor] = useState(null);
  const [reason, setReason] = useState(CAB_REJECT_REASONS[0]);
  if (!all) return <FullLoader />;
  const pending = all.filter((r) => r.status === "pending");
  const decided = all.filter((r) => r.status !== "pending");

  const approve = async (r) => {
    try {
      const { data } = await http.post(`/cab-requests/${r.id}/approve`);
      toast.success(`Approved — ${data.assigned} cabs assigned${data.waitlist ? ` · ${data.waitlist} waitlisted` : ""}`);
      reload(); onDecided && onDecided();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const doReject = async () => {
    try {
      await http.post(`/cab-requests/${rejectFor.id}/reject`, { reason });
      toast.success("Request rejected"); setRejectFor(null); reload(); onDecided && onDecided();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  return (
    <>
      <PageHead title="Cab Requests" sub={`Clients asking for more cabs · ${pending.length} pending`} />
      {pending.length === 0 && <EmptyState icon={Inbox} title="No pending requests" sub="All cab requests are caught up." />}
      <div className="space-y-3">
        {pending.map((r) => (
          <Card key={r.id} className="flex flex-col gap-3 p-4 sm:flex-row sm:items-center">
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-2">
                <span className="font-semibold text-stone-900">{r.advertiser_name}</span>
                <StatusBadge status="pending" />
              </div>
              <div className="mt-1 text-sm text-stone-600">wants <b className="font-mono tabular-nums text-stone-900">{r.count}</b> more cabs</div>
              {r.note && <div className="mt-0.5 truncate text-xs text-stone-500">“{r.note}”</div>}
              <div className="mt-1 text-[11px] text-stone-400">{new Date(r.created_at).toLocaleString("en-IN")}</div>
            </div>
            <div className="flex flex-none items-center gap-2">
              <Button data-testid="ops-cab-request-approve" onClick={() => approve(r)}><CheckCircle2 className="h-4 w-4" /> Approve</Button>
              <Button data-testid="ops-cab-request-reject" onClick={() => setRejectFor(r)} variant="outline" className="text-rose-600"><XCircle className="h-4 w-4" /> Reject</Button>
            </div>
          </Card>
        ))}
      </div>

      {decided.length > 0 && (
        <>
          <h3 className="mb-3 mt-8 font-display text-[15px] font-semibold tracking-tight text-stone-900">Decision history</h3>
          <div className="space-y-3">
            {decided.map((r) => (
              <Card key={r.id} className="flex items-center justify-between gap-3 p-4">
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="font-semibold text-stone-900">{r.advertiser_name}</span>
                    <StatusBadge status={r.status} />
                  </div>
                  <div className="mt-1 text-xs text-stone-500">
                    {r.count} cabs requested{r.assigned != null ? ` · ${r.assigned} assigned` : ""}
                    {r.reject_reason && <span className="text-rose-600"> · {r.reject_reason}</span>}
                  </div>
                  <div className="mt-0.5 text-[11px] text-stone-400">decided by {r.decided_by} · {new Date(r.decided_at).toLocaleString("en-IN")}</div>
                </div>
              </Card>
            ))}
          </div>
        </>
      )}

      <Modal open={!!rejectFor} onClose={() => setRejectFor(null)} title="Reject cab request — pick a reason">
        <div className="space-y-2">
          {CAB_REJECT_REASONS.map((r) => (
            <Button key={r} variant={reason === r ? "default" : "outline"} onClick={() => setReason(r)} className="w-full justify-start font-normal">{r}</Button>
          ))}
        </div>
        <Button variant="destructive" onClick={doReject} className="mt-4 w-full">Reject request</Button>
      </Modal>
    </>
  );
}